import {
  AbTestingConfig,
  abTestingFeatures,
} from '@wwe-portal/entities/ab-testing.type';
import { ConfigData } from '@wwe-portal/entities/global.type';
import {
  featureConfigKeys,
  FeatureConfigMap,
} from '@wwe-portal/entities/feature-configs.type';
import { STORAGE_ITEM_LOCAL_CONFIG } from '@wwe-portal/web/utils/constants/keys';
import { createStore, useStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface LocalConfigStore {
  config: Record<string, boolean>;
  abTesting: Record<string, boolean>;
  featureConfigs: FeatureConfigMap;
  updateConfig: (name: string, value: boolean) => void;
  updateAbTesting: (name: string, value: boolean) => void;
  updateFeatureConfig: (name: string, config: Record<string, unknown>) => void;
  clearAll: () => void;
}

export const staticAbTesting: AbTestingConfig = {
  [abTestingFeatures.SGSPT_ABTesting_SSGuide]: false,
  [abTestingFeatures.SGSPT_ABTesting_Overhaul]: false,
};

export const staticFeatureConfigs: FeatureConfigMap = {
  [featureConfigKeys.feature_drip_7d]: {},
  [featureConfigKeys.feature_drip_14d]: {},
  [featureConfigKeys.feature_drip_30d]: {},
};

export const staticConfig: ConfigData = {
  'debug.settings': false,
  'debug.sessionRecord': false,
  rbe: false,
  news: false,
  shop: false,
  global: false,
  trivia: false,
  webshop: false,
  calendar: false,
  playGame: false,
  dashboard: false,
  fanVoting: false,
  tournament: false,
  flashMarket: false,
  leaderboard: false,
  playerProfile: false,
  factionProfile: false,
  superStarGuide: false,
  'superStarGuide.1.1a': false,
  'superStarGuide.1.1b': false,
  'superStarGuide.offers': false,
  'segmentation.p2': false,
  playgami: false,
  abTesting: false,
  subscription: false,
  clubStore: false,
  'clubStore.offers': false,
  'clubStore.offers.premium': false,
  'clubStore.offers.prestige': false,
  'clubStore.offers.super_vip': false,
  'xsolla.paymentLiveMode': false,
  xsolla: false,
  debug: false,
  webShop: false,
  segmentation: false,
  'segmentation.offers': false,
  displayIAPOffers: false,
  tutorial: false,
  'overhaul.leftSidebar': false,
  'overhaul.tutorial': false,
  // Search box, language switcher and raw item ids on /item-probabilities. Off by
  // default: the page is a public disclosure page and those are working aids.
  itemProbabilitiesDevControls: false,
};

export const localConfigStore = createStore(
  persist<
    LocalConfigStore,
    [],
    [],
    Pick<LocalConfigStore, 'config' | 'abTesting' | 'featureConfigs'>
  >(
    (set, get) => ({
      config: {},
      abTesting: {},
      featureConfigs: {},
      updateConfig: (name: string, value: boolean) => {
        const config = get().config;
        config[name] = value;
        set({ config: { ...config } });
      },
      updateAbTesting: (name: string, value: boolean) => {
        const abTesting = get().abTesting;
        abTesting[name] = value;
        set({ abTesting: { ...abTesting } });
      },
      updateFeatureConfig: (name: string, config: Record<string, unknown>) => {
        const featureConfigs = get().featureConfigs;
        featureConfigs[name] = config;
        set({ featureConfigs: { ...featureConfigs } });
      },
      clearAll: () => {
        set({ config: {}, abTesting: {}, featureConfigs: {} });
      },
    }),
    {
      name: STORAGE_ITEM_LOCAL_CONFIG,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        config: state.config,
        abTesting: state.abTesting,
        featureConfigs: state.featureConfigs,
      }),
    }
  )
);

export const useLocalConfig = <T = LocalConfigStore>(
  selector: (state: LocalConfigStore) => T
) => {
  return useStore(localConfigStore, selector);
};
